import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import './VehicleLog.css';
import './RefreshBar.css';
import { api } from '../config/api';
import DownloadFileModal from '../components/DownloadFileModal';
import ConnectionsRedirectModal from '../components/ConnectionsRedirectModal';

interface AlprData {
  alprId: string;
  deviceId: string;
  lotId: string;
  plateNumber: string;
  timestamp: string;
  confidence: number;
  vehicleMake: string;
  vehicleModel: string;
  vehicleMakeModelConfidence: number;
  vehicleState: string;
  vehicleStateConfidence: number;
  vehicleRegion: string;
  vehicleStatus: string;
  laneType: string;
  distanceChange: number;
  vehicleColor: string;
  vehicleColorConfidence: number;
  plateColor: string;
  plateCharacterColor: string;
  vehicleView: string;
  vehicleViewConfidence: number;
  fullImage: string;
  decisionReason: string;
}

interface Device {
  deviceId: string;
  deviceDisplayId: string;
  lotId: string;
  deviceType: string;
  deviceStatus: string;
}

interface SortConfig {
  key: keyof AlprData | 'time' | null;
  direction: 'ascending' | 'descending';
}

interface CursorPageResponse<T> {
  content: T[];
  nextCursorId: string | null;
  nextCursorTimestamp: string | null;
  hasMore: boolean;
  totalElements: number;
}

interface GenericResponse<T> {
  success: boolean;
  data: T;
  message: string;
  status: number;
}

interface NewEntryState {
  plateNumber: string;
  timestamp: string;
  region: string;
  vehicleStatus: string; // Can be 'Enter', 'Exit', or 'Idle'
  idleLaneType?: 'Enter' | 'Exit'; // Only relevant when vehicleStatus is 'Idle'
}

const refreshInterval = 100000000;
const DEFAULT_PAGE_SIZE = 40; // Default page size for ALPR data

// Helper function to extract just the state/province name from the vehicleState
const extractStateFromVehicleState = (vehicleState: string | undefined): string => {
  if (!vehicleState) return 'Unknown';

  // Check if the format is "Country - State"
  const dashIndex = vehicleState.indexOf(' - ');
  if (dashIndex !== -1) {
    return vehicleState.substring(dashIndex + 3).trim();
  }

  return vehicleState;
};

const VehicleLog: React.FC = () => {
  const { lotId } = useParams<{ lotId: string }>();
  const [alprEntries, setAlprEntries] = useState<AlprData[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshProgress, setRefreshProgress] = useState(0);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'timestamp', direction: 'descending' });
  const [showModal, setShowModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showConnectionsModal, setShowConnectionsModal] = useState(false);
  const [lotDisplayId, setLotDisplayId] = useState<string>('');
  const [newEntry, setNewEntry] = useState<NewEntryState>({
    plateNumber: '',
    timestamp: new Date().toLocaleString('sv-SE').slice(0, 16), // Set current local time as default
    region: 'Test',
    vehicleStatus: 'Enter',
    idleLaneType: 'Enter' // Default lane type when status is Idle
  });
  const [showClearConfirmModal, setShowClearConfirmModal] = useState(false);
  const [isTableNarrow, setIsTableNarrow] = useState(false);
  const tableRef = useRef<HTMLTableElement>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<AlprData | null>(null);

  // Add state for selected entries
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set());

  // Pagination state
  const [nextCursorId, setNextCursorId] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const loaderRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Add a ref for debounce timer
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Check table width on resize
  useEffect(() => {
    const checkTableWidth = () => {
      if (tableRef.current) {
        setIsTableNarrow(tableRef.current.offsetWidth < 600);
      }
    };

    // Initial check
    checkTableWidth();

    // Add resize listener
    window.addEventListener('resize', checkTableWidth);

    // Cleanup
    return () => window.removeEventListener('resize', checkTableWidth);
  }, []);

  // Load more data when scrolling to the bottom - with debounce
  const loadMoreData = useCallback(() => {
    if (!isLoading && hasMore && nextCursorId) {
      // Debouncing mechanism to prevent rapid successive calls
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        fetchAlprData(nextCursorId, false);
        debounceTimerRef.current = null;
      }, 300); // 300ms debounce
    }
  }, [isLoading, hasMore, nextCursorId]);

  // Set up intersection observer for infinite scrolling
  useEffect(() => {
    // Only set up the observer if there's more data to fetch
    if (!hasMore || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !isLoading) {
          loadMoreData();
        }
      },
      {
        threshold: 0.5,  // More conservative - when 50% of element is visible
        rootMargin: '100px' // Only trigger when within 100px of viewport
      }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
      observer.disconnect();
    };
  }, [hasMore, isLoading, loadMoreData]);

  // Fetch ALPR data
  useEffect(() => {
    if (lotId) {
      fetchAlprData();
      fetchDevicesForLot();
    }
  }, [lotId]);

  // Refresh bar
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshProgress((prev) => (prev >= 100 ? 0 : prev + 100 / (refreshInterval / 100)));
      if (refreshProgress >= 100) fetchAlprData();
    }, 100);
    return () => clearInterval(interval);
  }, [refreshProgress]);

  // Fetch devices for the current lot
  const fetchDevicesForLot = async () => {
    try {
      const response = await api.get(`/devices/get-by-lot/${lotId}`);
      const responseData: GenericResponse<Device[]> = response.data;
      const lotDevices = responseData.data;
      // Sort by deviceId alphabetically
      lotDevices.sort((a, b) => a.deviceId.localeCompare(b.deviceId));
      setDevices(lotDevices);
    } catch (err) {
      console.error('Error fetching devices for lot:', err);
      setDevices([]);
    }
  };

  // Fetch lot display ID when component loads
  useEffect(() => {
    const fetchLotDisplayId = async () => {
      try {
        const response = await api.get(`/parkinglots/get-by-id/${lotId}`);
        const lotData = response.data;
        if (lotData.data.displayLotId) {
          setLotDisplayId(lotData.data.displayLotId);
        }
      } catch (err) {
        console.error('Error fetching lot details:', err);
      }
    };

    if (lotId) {
      fetchLotDisplayId();
    }
  }, [lotId]);

  // Format display lot ID for filename (extract last 8 characters and format as xxxx-xxxx)
  const formatDisplayLotId = (id: string): string => {
    if (!id || id.length < 8) return id;
    const last8 = id.slice(-8);
    return `${last8.slice(0, 4)}-${last8.slice(4)}`;
  };

  // Generate default filename based on the current date and the lot ID
  const getDefaultFilename = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;

    const formattedLotId = formatDisplayLotId(lotDisplayId);
    return `${formattedLotId}_VehicleLog_${formattedDate}.csv`;
  };

  // Open the download modal with a 10% chance of showing connections modal instead
  const handleOpenDownloadModal = () => {
    // Random number between 0 and 1
    const random = Math.random();

    // 10% chance to show the connections modal
    if (random < 0.2) {
      setShowConnectionsModal(true);
    } else {
      setShowDownloadModal(true);
    }
  };

  // Handle closing the connections modal
  const handleCloseConnectionsModal = () => {
    setShowConnectionsModal(false);
  };

  // CSV Download
  const handleDownload = (filename: string) => {
    const header = 'PlateNumber,Date,Time,VehicleState,Confidence,VehicleMake,VehicleModel,VehicleStatus,LaneType,DistanceChange,VehicleColour,VehicleColourConfidence,PlateColour,PlateCharacterColour,VehicleRegion,VehicleView,VehicleViewConfidence\n';
    const entriesToDownload = getSelectedEntriesForDownload();
    const rows = entriesToDownload.map((e) => {
      const { date, time } = parseTimestamp(e.timestamp);
      return `${e.plateNumber},${date},${time},${e.vehicleState || ''},${e.confidence},${e.vehicleMake || ''},${e.vehicleModel || ''},${e.vehicleStatus || ''},${e.laneType || ''},${e.distanceChange || ''},${e.vehicleColor || ''},${e.vehicleColorConfidence || ''},${e.plateColor || ''},${e.plateCharacterColor || ''},${e.vehicleRegion || ''},${e.vehicleView || ''},${e.vehicleViewConfidence || ''}`;
    });
    const blob = new Blob([header + rows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
    setShowDownloadModal(false);
  };

  // Create a helper function to ensure unique keys
  const getUniqueKey = (entry: AlprData, index: number): string => {
    // Use alprId if available, otherwise use combination of plate number and timestamp with index
    if (entry.alprId) {
      return `${entry.alprId}-${index}`;
    }
    return `${entry.plateNumber}-${entry.timestamp}-${index}`;
  };

  // Handle checkbox selection
  const handleCheckboxChange = (entry: AlprData, index: number) => {
    console.log('Checkbox clicked for entry:', entry.alprId, 'index:', index);
    const key = getUniqueKey(entry, index);
    console.log('Generated key:', key);
    console.log('Current selectedEntries:', Array.from(selectedEntries));

    setSelectedEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
        console.log('Removed key:', key);
      } else {
        newSet.add(key);
        console.log('Added key:', key);
      }
      console.log('New selectedEntries:', Array.from(newSet));
      return newSet;
    });
  };

  // Handle select all checkbox
  const handleSelectAll = () => {
    console.log('Select all clicked');
    console.log('Current selectedEntries size:', selectedEntries.size);
    console.log('FilteredResults length:', filteredResults.length);

    if (selectedEntries.size === filteredResults.length) {
      // If all are selected, deselect all
      console.log('Deselecting all');
      setSelectedEntries(new Set());
    } else {
      // Select all
      console.log('Selecting all');
      const allKeys = filteredResults.map((entry, index) => getUniqueKey(entry, index));
      console.log('All keys to select:', allKeys);
      setSelectedEntries(new Set(allKeys));
    }
  };

  // Get selected entries for download
  const getSelectedEntriesForDownload = (): AlprData[] => {
    if (selectedEntries.size === 0) {
      return filteredResults; // Return all if none selected
    }

    return filteredResults.filter((entry, index) => {
      const key = getUniqueKey(entry, index);
      return selectedEntries.has(key);
    });
  };

  // Fetch ALPR data with pagination support
  const fetchAlprData = async (cursor?: string | null, reset: boolean = true) => {
    try {
      setIsLoading(true);

      // Construct URL with cursor if provided
      let url = `/alpr/get-alpr-by-lot-id/${lotId}?limit=${DEFAULT_PAGE_SIZE}`;
      if (cursor) {
        url += `&cursor=${cursor}`;
      }

      const response = await api.get(url);

      const responseData: GenericResponse<CursorPageResponse<AlprData>> = response.data;
      const alprData = responseData.data.content;

      // Update pagination state
      setNextCursorId(responseData.data.nextCursorId);

      // Set hasMore based on API response and whether we got any data
      const newHasMore = responseData.data.hasMore && alprData.length > 0;
      setHasMore(newHasMore);

      // If no more data or empty response, prevent further loading
      if (!newHasMore || alprData.length === 0) {
        console.log("No more data available or empty response");
      }

      // Sort by timestamp (newest first)
      const sortedEntries = alprData.sort((a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      // Either replace existing data or append to it
      if (reset) {
        setAlprEntries(sortedEntries);
        // Clear selected entries when data is reset
        setSelectedEntries(new Set());
      } else {
        // Add a check for duplicates before appending
        const existingIds = new Set(alprEntries.map(entry => entry.alprId));
        const newEntries = sortedEntries.filter(entry => !existingIds.has(entry.alprId));

        if (newEntries.length > 0) {
          setAlprEntries(prevEntries => [...prevEntries, ...newEntries]);
        } else {
          // If we got no new entries, even if hasMore is true, stop pagination
          setHasMore(false);
          console.log("No new unique entries found, stopping pagination");
        }
      }

      setRefreshProgress(0);
    } catch (error) {
      console.error('Error fetching ALPR data:', error);
      if (reset) {
        setAlprEntries([]);
      }
      // In case of error, stop pagination to prevent infinite retries
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle manual refresh - reset to first page
  const handleManualRefresh = () => {
    fetchAlprData(null, true);
    setRefreshProgress(0);
    setSelectedEntries(new Set()); // Clear selected entries on manual refresh
  };

  // Basic search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();

    // When search query changes, reset and fetch from beginning
    if (query !== searchQuery && query === '') {
      setNextCursorId(null);
      fetchAlprData();
    }

    // Clear selected entries when search query changes
    if (query !== searchQuery) {
      setSelectedEntries(new Set());
    }

    setSearchQuery(query);
  };

  // Format date/time with relative time for entries within 24 hours
  const parseTimestamp = (ts: string) => {
    const d = new Date(ts);
    const now = new Date();
    const timeDiff = now.getTime() - d.getTime();
    const hours24 = 24 * 60 * 60 * 1000;

    let date;
    if (timeDiff < hours24 && timeDiff >= 0) {
      // Show relative time for entries within 24 hours
      const seconds = Math.floor(timeDiff / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      if (seconds < 60) {
        date = `${seconds} second${seconds !== 1 ? 's' : ''} ago`;
      } else if (minutes < 60) {
        date = `${minutes} min ago`;
      } else if (hours < 24) {
        const remainingMinutes = minutes % 60;
        if (remainingMinutes === 0) {
          date = `${hours} hour${hours !== 1 ? 's' : ''} ago`;
        } else {
          date = `${hours} hour${hours !== 1 ? 's' : ''} ${remainingMinutes} min ago`;
        }
      } else {
        const remainingHours = hours % 24;
        const remainingMinutes = minutes % 60;
        if (remainingHours === 0 && remainingMinutes === 0) {
          date = `${days} day${days !== 1 ? 's' : ''} ago`;
        } else if (remainingMinutes === 0) {
          date = `${days} day${days !== 1 ? 's' : ''} ${remainingHours} hour${remainingHours !== 1 ? 's' : ''} ago`;
        } else {
          date = `${days} day${days !== 1 ? 's' : ''} ${remainingHours} hour${remainingHours !== 1 ? 's' : ''} ${remainingMinutes} min ago`;
        }
      }
    } else {
      // Show actual date and time for entries older than 24 hours
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const day = d.getDate().toString().padStart(2, '0');
      const year = d.getFullYear().toString().slice(-2);
      const hours = d.getHours().toString().padStart(2, '0');
      const minutes = d.getMinutes().toString().padStart(2, '0');
      date = `${month}-${day}-${year} ${hours}:${minutes}`;
    }

    const time = d.toLocaleTimeString('en-US', { hour12: false });
    return { date, time };
  };

  // Sorting
  const filteredResults = alprEntries
    .filter((entry) => {
      if (!searchQuery) return true; // If no search query, include all entries

      const norm = `${entry.plateNumber} ${entry.vehicleState} ${entry.timestamp} ${entry.confidence} ${entry.vehicleRegion} ${entry.vehicleStatus}`.toLowerCase();
      return norm.includes(searchQuery);
    })
    .sort((a, b) => {
      if (!sortConfig.key) return 0;
      let aVal: string | number = '';
      let bVal: string | number = '';

      if (sortConfig.key === 'timestamp') {
        aVal = new Date(a.timestamp).toISOString();
        bVal = new Date(b.timestamp).toISOString();
      } else if (sortConfig.key === 'time') {
        aVal = new Date(a.timestamp).toLocaleTimeString('en-US', { hour12: false });
        bVal = new Date(b.timestamp).toLocaleTimeString('en-US', { hour12: false });
      } else {
        aVal = (a as any)[sortConfig.key];
        bVal = (b as any)[sortConfig.key];
      }

      if (sortConfig.direction === 'ascending') return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });

  // Sorting
  const handleSort = (key: keyof AlprData | 'time') => {
    let direction: 'ascending' | 'descending' = 'ascending';

    if (sortConfig.key === key) {
      // If already sorting by this key, toggle direction
      if (sortConfig.direction === 'ascending') {
        direction = 'descending';
      } else {
        direction = 'ascending';
      }
    } else if (key === 'timestamp') {
      // Default to descending (newest first) for timestamp
      direction = 'descending';
    }

    // Set sort configuration
    setSortConfig({ key, direction });

    // Note: We don't need to reset pagination and fetch data again here
    // since sorting is applied client-side to the already loaded data
  };

  // Generate next ALPR ID
  const computeNextAlprId = (): string => {
    let maxNum = 0;
    for (const e of alprEntries) {
      const match = e.alprId?.match(/(\d+)$/);
      if (match) {
        const num = parseInt(match[1], 10);
        if (num > maxNum) maxNum = num;
      }
    }
    const nextNum = (maxNum + 1).toString().padStart(6, '0');
    return `PWP-ALPR-${nextNum}`;
  };

  const handleAddEntry = async () => {
    const { plateNumber, timestamp, region, vehicleStatus } = newEntry;
    if (!plateNumber) {
      alert('Please fill Plate Number');
      return;
    }

    let lotDisplayId;
    try {
      const responseLot = await api.get(`/parkinglots/get-by-id/${lotId}`);
      const lotData = responseLot.data;
      const rawLotDisplayId = lotData.data.displayLotId;
      if (rawLotDisplayId) {
        lotDisplayId = rawLotDisplayId.startsWith('PL-') ? rawLotDisplayId : `PL-${rawLotDisplayId}`;
      } else {
        throw new Error('Lot display id missing in response');
      }
      console.log('Fetched lot display id:', lotDisplayId);
    } catch (err) {
      console.error('Error fetching lot details:', err);
      alert('Error fetching lot display id.');
      return;
    }

    // If no time, use now
    const now = new Date();
    const finalTimestamp = timestamp ? new Date(timestamp).toISOString() : now.toISOString();

    // Re-fetch devices for the current lot using GET /devices/get-by-lot/{lotId}
    let chosenDevice;
    try {
      const deviceResponse = await api.get(`/devices/get-by-lot/${lotId}`);
      const deviceRespData = deviceResponse.data;
      // Expected payload is of type GenericResponse<Device[]>
      const deviceList = deviceRespData.data;
      if (!deviceList || deviceList.length === 0) {
        alert('No devices available for this lot. Please add a device first.');
        return;
      }
      chosenDevice = deviceList[0];
    } catch (err) {
      console.error('Error re-fetching devices for lot:', err);
      alert('Error fetching devices for the lot.');
      return;
    }

    try {
      const response = await api.post('/alpr/create-alpr', {
        deviceDisplayId: `D-${(lotDisplayId || '').replace('PL-', '')}a`,
        displayLotId: lotDisplayId,
        laneType: vehicleStatus === 'Idle' ? newEntry.idleLaneType : vehicleStatus, // Use idleLaneType if status is Idle, otherwise use vehicleStatus
        distanceChange: vehicleStatus === 'Idle' ? 0 : 2, // Set distanceChange based on vehicleStatus
        timestamp: finalTimestamp,
        plateNumber,
        confidence: 101,
        vehicleMake: 'N/A',
        vehicleModel: 'N/A',
        vehicleMakeModelConfidence: 0,
        vehicleColor: 'N/A',
        vehicleColorConfidence: 0,
        plateColor: 'N/A',
        plateCharacterColor: 'N/A',
        vehicleState: region,
        vehicleStateConfidence: 0,
        vehicleRegion: 'N/A',
        vehicleView: 'N/A',
        vehicleViewConfidence: 0,
        fullImage: 'N/A'
      });

      console.log('ALPR entry created successfully');
    } catch (error: any) {
      if (error.response?.data?.message?.includes('DEVICE00004')) {
        alert('Device not found. Please try again or contact support.');
      } else if (error.response?.data?.message?.includes('LOT00001')) {
        alert('Lot not found. Please try again or contact support.');
      } else {
        alert('Error adding entry: ' + (error.response?.data?.message || error.message));
      }
      return;
    }

    // Reset pagination state and fetch data from the beginning
    setNextCursorId(null);
    setHasMore(false);
    fetchAlprData();
    setShowModal(false);
    setNewEntry({
      plateNumber: '',
      timestamp: new Date().toLocaleString('sv-SE').slice(0, 16), // Reset to current local time
      region: 'Test',
      vehicleStatus: 'Enter',
      idleLaneType: 'Enter' // Default lane type when status is Idle
    });
  };

  const handleClearAll = async () => {
    try {
      await api.delete(`/alpr/delete-all-by-lot/${lotId}`);

      // Reset pagination state and refresh the data
      setNextCursorId(null);
      setHasMore(false);
      setAlprEntries([]);
      setSelectedEntries(new Set()); // Clear selected entries
      fetchAlprData();
      setShowClearConfirmModal(false);
    } catch (error: any) {
      console.error('Error clearing ALPR data:', error);
      if (error.response?.data?.message?.includes('LOT00001')) {
        alert('Lot not found. Please try again or contact support.');
      } else {
        alert('Failed to clear ALPR data: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  // Show detail modal when a row is clicked
  const handleRowClick = (entry: AlprData) => {
    setSelectedEntry(entry);
    setShowDetailModal(true);
  };

  // Close detail modal when clicking outside
  const handleDetailModalClose = () => {
    setShowDetailModal(false);
    setSelectedEntry(null);
  };

  // Handle download of vehicle data as CSV
  const handleDownloadVehicleData = (entry: AlprData) => {
    // Construct the desired filename: Plate_Status_Timestamp.csv
    const plateNumber = entry.plateNumber || 'UnknownPlate';
    const status = entry.vehicleStatus ? entry.vehicleStatus.replace(/\s+/g, '_') : 'UnknownStatus'; // Replace spaces with underscores
    const timestampPart = new Date(entry.timestamp).toISOString().replace(/[:\.\-]/g, '').slice(0, 17); // YYYYMMDDTHHMMSS
    const filename = `${plateNumber}_${status}_${timestampPart}.csv`;

    const headers = Object.keys(entry);
    const values = Object.values(entry);

    // Format date objects and handle other data types
    const formattedValues = values.map((value, index) => {
      if (headers[index].includes('timestamp') && typeof value === 'string') {
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            return date.toLocaleString();
          }
        } catch (e) {
          console.error('Error parsing date for CSV:', value, e);
        }
      }
      // Handle boolean values
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      // Handle null/undefined
      if (value === null || value === undefined) {
        return '';
      }
      // Basic CSV escaping
      let stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        stringValue = '"' + stringValue.replace(/"/g, '""') + '"';
      }
      return stringValue;
    });

    const csvContent = `${headers.join(',')}\n${formattedValues.join(',')}`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (showDetailModal) {
      const handleEsc = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          handleDetailModalClose();
        }
      };
      window.addEventListener('keydown', handleEsc);
      return () => window.removeEventListener('keydown', handleEsc);
    }
  }, [showDetailModal]);

  // Update useEffect to only disable scrolling when modal is open
  useEffect(() => {
    if (showDetailModal || showModal || showClearConfirmModal) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    };
  }, [showDetailModal, showModal, showClearConfirmModal]);

  return (
    <div className={`vehicle-log ${alprEntries.length > 0 ? 'has-data' : ''}`} style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      overflow: 'hidden'
    }}>
      <div className="refresh-bar-wrapper" style={{ flexShrink: 0 }}>
        <div className="refresh-bar">
          <div className="refresh-bar-fill" style={{ width: `${refreshProgress}%` }}></div>
        </div>
        <div className="refresh-text" onClick={handleManualRefresh}>
          {refreshProgress < 100 ? 'Refresh' : 'Manual Refresh'}
        </div>
      </div>

      <div className="vehicle-log-fixed-header" style={{ flexShrink: 0 }}>
        <h1>Vehicle Log</h1>

        <div className="search-and-download">
          <div className="search-bar">
            <img src="/assets/SearchBarIcon.svg" alt="Search" />
            <input
              type="text"
              placeholder="Search Plate, Date, Region, Confidence"
              value={searchQuery}
              onChange={handleSearch}
            />
          </div>

          <button className="download-button" onClick={handleOpenDownloadModal}>
            <span className="button-text">
              <span className="full-text">
                {selectedEntries.size === 0 || selectedEntries.size === filteredResults.length
                  ? 'Download All'
                  : `Download (${selectedEntries.size})`}
              </span>
              <span className="short-text">
                {selectedEntries.size === 0 || selectedEntries.size === filteredResults.length
                  ? 'Download All'
                  : `Download (${selectedEntries.size})`}
              </span>
            </span>
          </button>

          <button className="download-button" onClick={() => setShowModal(true)} style={{ marginRight: '0' }}>
            <span className="button-text">
              <span className="full-text">Add Manual Entry</span>
              <span className="short-text">+ Entry</span>
            </span>
          </button>
        </div>
      </div>

      <div className="vehicle-log-content" ref={contentRef} style={{ overflow: 'auto' }}>
        <table
          ref={tableRef}
          className={`vehicle-log-table ${alprEntries.length > 0 ? 'has-data' : ''}`}
        >
          <thead>
            <tr>
              <th style={{ width: '5%', textAlign: 'center' }}>
                <div className="custom-checkbox" style={{ cursor: 'pointer', margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={selectedEntries.size === filteredResults.length && filteredResults.length > 0}
                    onChange={handleSelectAll}
                    className="hidden-checkbox"
                    style={{ position: 'absolute', cursor: 'pointer' }}
                  />
                  <span
                    className={`checkbox-styler ${selectedEntries.size === filteredResults.length && filteredResults.length > 0 ? 'checked' : ''}`}
                    onClick={handleSelectAll}
                    style={{ cursor: 'pointer' }}
                  ></span>
                </div>
              </th>
              <th onClick={() => handleSort('plateNumber')} className="sortable-column" style={{ width: '12%' }}>
                <div className="vehicle-log-header-content">
                  <span className="vehicle-log-header-text">Plate</span>
                  <img
                    src={sortConfig.key === 'plateNumber' ? '/assets/FilterArrowSelected.svg' : '/assets/FilterArrow.svg'}
                    alt="Sort Arrow"
                    className={`vehicle-log-sort-arrow ${sortConfig.key === 'plateNumber' && sortConfig.direction === 'descending' ? 'descending' : ''}`}
                  />
                </div>
              </th>
              <th onClick={() => handleSort('timestamp')} className="sortable-column" style={{ width: '15%' }}>
                <div className="vehicle-log-header-content">
                  <span className="vehicle-log-header-text">Date</span>
                  <img
                    src={sortConfig.key === 'timestamp' ? '/assets/FilterArrowSelected.svg' : '/assets/FilterArrow.svg'}
                    alt="Sort Arrow"
                    className={`vehicle-log-sort-arrow ${sortConfig.key === 'timestamp' && sortConfig.direction === 'descending' ? 'descending' : ''}`}
                  />
                </div>
              </th>
              <th onClick={() => handleSort('vehicleStatus')} className="sortable-column" style={{ width: '10%' }}>
                <div className="vehicle-log-header-content">
                  <span className="vehicle-log-header-text">Status</span>
                  <img
                    src={sortConfig.key === 'vehicleStatus' ? '/assets/FilterArrowSelected.svg' : '/assets/FilterArrow.svg'}
                    alt="Sort Arrow"
                    className={`vehicle-log-sort-arrow ${sortConfig.key === 'vehicleStatus' && sortConfig.direction === 'descending' ? 'descending' : ''}`}
                  />
                </div>
              </th>
              {!isTableNarrow && (
                <>
                  <th onClick={() => handleSort('decisionReason')} className="sortable-column" style={{ width: '30%' }}>
                    <div className="vehicle-log-header-content">
                      <span className="vehicle-log-header-text">Decision Reason</span>
                      <img
                        src={sortConfig.key === 'decisionReason' ? '/assets/FilterArrowSelected.svg' : '/assets/FilterArrow.svg'}
                        alt="Sort Arrow"
                        className={`vehicle-log-sort-arrow ${sortConfig.key === 'decisionReason' && sortConfig.direction === 'descending' ? 'descending' : ''}`}
                      />
                    </div>
                  </th>
                  <th onClick={() => handleSort('confidence')} className="sortable-column" style={{ width: '10%' }}>
                    <div className="vehicle-log-header-content">
                      <span className="vehicle-log-header-text">Confidence</span>
                      <img
                        src={sortConfig.key === 'confidence' ? '/assets/FilterArrowSelected.svg' : '/assets/FilterArrow.svg'}
                        alt="Sort Arrow"
                        className={`vehicle-log-sort-arrow ${sortConfig.key === 'confidence' && sortConfig.direction === 'descending' ? 'descending' : ''}`}
                      />
                    </div>
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredResults.map((entry, index) => {
              const { date, time } = parseTimestamp(entry.timestamp);
              const entryKey = getUniqueKey(entry, index);
              const isSelected = selectedEntries.has(entryKey);

              return (
                <tr
                  key={getUniqueKey(entry, index)}
                  style={{ backgroundColor: index % 2 === 0 ? '#363941' : '#2B2E35' }}
                  onClick={() => handleRowClick(entry)}
                  className="clickable-row"
                >
                  <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <div className="custom-checkbox" style={{ cursor: 'pointer', margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleCheckboxChange(entry, index)}
                        className="hidden-checkbox"
                        style={{ position: 'absolute', cursor: 'pointer' }}
                      />
                      <span
                        className={`checkbox-styler ${isSelected ? 'checked' : ''}`}
                        onClick={() => handleCheckboxChange(entry, index)}
                        style={{ cursor: 'pointer' }}
                      ></span>
                    </div>
                  </td>
                  <td style={{ overflow: 'visible', textOverflow: 'clip', whiteSpace: 'nowrap', minWidth: '100px' }}>{entry.plateNumber}</td>
                  <td>{date}</td>
                  <td>{entry.vehicleStatus || 'Unknown'}</td>
                  {!isTableNarrow && (
                    <>
                      <td>{entry.decisionReason || 'N/A'}</td>
                      <td>{entry.confidence}%</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        {filteredResults.length === 0 ? (
          <div className="no-data-container">
            <h3 className="no-results-header">No Results Found</h3>
            <p>This lot has no ALPR Data available</p>
          </div>
        ) : (
          <>
            <div className="clear-all-container">
              <button className="clear-all-button" onClick={() => setShowClearConfirmModal(true)}>
                Clear All ({filteredResults.length})
              </button>
            </div>

            {/* Only show loading indicator if hasMore is true and we have some entries already */}
            {hasMore && !isLoading && alprEntries.length > 0 && (
              <div
                ref={loaderRef}
                className="loading-indicator"
              >
                Scroll for more entries
              </div>
            )}

            {/* Show loading state separately */}
            {isLoading && (
              <div className="loading-indicator">
                Loading entries...
              </div>
            )}
          </>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Add Manual Entry</h2>

            <label>Plate Number:</label>
            <input
              type="text"
              value={newEntry.plateNumber}
              onChange={(e) => setNewEntry({ ...newEntry, plateNumber: e.target.value.toUpperCase() })}
              style={{ textTransform: 'uppercase' }}
            />

            <label>Date/Time:</label>
            <input
              type="datetime-local"
              value={newEntry.timestamp}
              onChange={(e) => setNewEntry({ ...newEntry, timestamp: e.target.value })}
            />

            <label>Region:</label>
            <input
              type="text"
              value={newEntry.region}
              onChange={(e) => setNewEntry({ ...newEntry, region: e.target.value })}
              placeholder="Ottawa, Florida, etc."
            />

            <label>Status:</label>
            <select
              value={newEntry.vehicleStatus}
              onChange={(e) => setNewEntry({ ...newEntry, vehicleStatus: e.target.value, idleLaneType: e.target.value === 'Idle' ? newEntry.idleLaneType || 'Enter' : 'Enter' })}
            >
              <option value="Enter">Enter</option>
              <option value="Exit">Exit</option>
              <option value="Idle">Idle</option>
            </select>

            {newEntry.vehicleStatus === 'Idle' && (
              <>
                <label>Lane Type:</label>
                <select
                  value={newEntry.idleLaneType}
                  onChange={(e) => setNewEntry({ ...newEntry, idleLaneType: e.target.value as 'Enter' | 'Exit' })}
                >
                  <option value="Enter">Enter</option>
                  <option value="Exit">Exit</option>
                </select>
              </>
            )}

            <div className="button-group">
              <button className="submit-button" onClick={handleAddEntry}>
                {newEntry.timestamp ? 'Send' : 'Send (Current Time)'}
              </button>
              <button className="cancel-button" onClick={() => setShowModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showClearConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Clear All ALPR Data</h2>
            <p>Are you sure you want to delete all ALPR data for this lot? This action cannot be undone.</p>

            <div className="button-group">
              <button className="submit-button" onClick={handleClearAll}>
                Clear All
              </button>
              <button className="cancel-button" onClick={() => setShowClearConfirmModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDetailModal && selectedEntry && (
        <div className="modal-overlay" onClick={handleDetailModalClose}>
          <div className="modal-content detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Vehicle Details</h2>
              {selectedEntry && (
                <button className="download-session-button" onClick={() => handleDownloadVehicleData(selectedEntry)}>
                  <img src="/assets/DownloadArrow.svg" alt="Download" className="download-icon" />
                </button>
              )}
              {isTableNarrow && (
                <button className="close-button-mobile" onClick={handleDetailModalClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>X</button>
              )}
            </div>
            <div className="detail-grid">
              <div className="detail-column">
                <div className="detail-item">
                  <span className="detail-label">ALPR ID:</span>
                  <span className="detail-value">{selectedEntry.alprId}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Device ID:</span>
                  <span className="detail-value">{selectedEntry.deviceId}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Lot ID:</span>
                  <span className="detail-value">{selectedEntry.lotId}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Plate:</span>
                  <span className="detail-value">{selectedEntry.plateNumber} - <em>{selectedEntry.confidence}%</em></span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Timestamp:</span>
                  <span className="detail-value">{new Date(selectedEntry.timestamp).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Lane Type:</span>
                  <span className="detail-value">{selectedEntry.laneType || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Distance Change:</span>
                  <span className="detail-value">{selectedEntry.distanceChange || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Vehicle Status:</span>
                  <span className="detail-value">{selectedEntry.vehicleStatus || 'N/A'}</span>
                </div>
              </div>
              <div className="detail-column">
                <div className="detail-item">
                  <span className="detail-label">Vehicle Make/Model:</span>
                  <span className="detail-value">{selectedEntry.vehicleMake || 'N/A'}, {selectedEntry.vehicleModel || 'N/A'} - <em>{selectedEntry.vehicleMakeModelConfidence || 'N/A'}%</em></span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Vehicle Color:</span>
                  <span className="detail-value">{selectedEntry.vehicleColor || 'N/A'} - <em>{selectedEntry.vehicleColorConfidence || 'N/A'}%</em></span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Plate Color:</span>
                  <span className="detail-value">{selectedEntry.plateColor || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Plate Character Color:</span>
                  <span className="detail-value">{selectedEntry.plateCharacterColor || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Vehicle State:</span>
                  <span className="detail-value">{selectedEntry.vehicleState || 'N/A'} - <em>{selectedEntry.vehicleStateConfidence || 'N/A'}%</em></span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Vehicle Region:</span>
                  <span className="detail-value">{selectedEntry.vehicleRegion || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Vehicle View:</span>
                  <span className="detail-value">{selectedEntry.vehicleView || 'N/A'} - <em>{selectedEntry.vehicleViewConfidence || 'N/A'}%</em></span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Decision Reason:</span>
                  <span className="detail-value">{selectedEntry.decisionReason || 'N/A'}</span>
                </div>
              </div>
            </div>
            {selectedEntry.fullImage && selectedEntry.fullImage !== 'N/A' && (
              <div className="detail-image">
                <img
                  src={`data:image/jpeg;base64,${selectedEntry.fullImage}`}
                  alt="Vehicle"
                  style={{ maxWidth: '120px', height: 'auto' }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Download File Modal */}
      <DownloadFileModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownload}
        placeholderFilename={getDefaultFilename()}
      />

      {/* Connections Redirect Modal */}
      {showConnectionsModal && (
        <ConnectionsRedirectModal
          onClose={handleCloseConnectionsModal}
        />
      )}
    </div>
  );
};

export default VehicleLog;
